import { z } from "zod";

export const engineIds = [
  "bazi",
  "ziwei",
  "iching",
  "feng_shui",
  "name_phone_wuxing",
  "pythagoras",
] as const;

const phoneSchema = z
  .string()
  .transform((value) => value.replace(/[\s().-]/g, ""))
  .pipe(
    z
      .string()
      .regex(/^\+?[1-9]\d{8,14}$/, "phoneNumber must contain 9 to 15 digits"),
  );

const geoPlaceSchema = z
  .object({
    countryCode: z
      .string()
      .trim()
      .length(2)
      .transform((value) => value.toUpperCase()),
    locality: z.string().trim().min(1).max(120),
    latitude: z.number().min(-90).max(90).optional(),
    longitude: z.number().min(-180).max(180).optional(),
    ianaTimeZone: z.string().trim().min(3).max(80),
  })
  .strict();

const homeContextSchema = z
  .object({
    address: z.string().trim().min(5).max(300),
    spaceType: z.enum(["house", "apartment", "room", "office", "shop"]),
    facingDirection: z
      .enum([
        "bac",
        "dong_bac",
        "dong",
        "dong_nam",
        "nam",
        "tay_nam",
        "tay",
        "tay_bac",
      ])
      .optional(),
    directionConfidence: z.enum(["exact", "approximate", "unknown"]),
  })
  .strict();

export const reportRequestSchema = z
  .object({
    fullName: z.string().trim().min(2).max(100),
    calculationGender: z.enum(["nam", "nu"]),
    calendar: z.enum(["solar", "lunar"]),
    birthDate: z.iso.date(),
    birthTime: z
      .string()
      .regex(/^([01]\d|2[0-3]):[0-5]\d$/, "birthTime must use HH:mm"),
    birthTimeConfidence: z.enum(["exact", "approximate", "unknown"]),
    birthPlace: geoPlaceSchema,
    phoneNumber: phoneSchema,
    currentProvince: z.string().trim().min(1).max(120),
    homeContext: homeContextSchema.optional(),
    question: z.string().trim().min(10).max(2000),
    adultConfirmed: z.literal(true),
    processingConsent: z.literal(true),
    aiConsent: z.literal(true),
  })
  .strict();

export type EngineId = (typeof engineIds)[number];
export type ReportRequest = z.infer<typeof reportRequestSchema>;
export type GeoPlace = ReportRequest["birthPlace"];
export type HomeContext = NonNullable<ReportRequest["homeContext"]>;

export type RuleTraceEntry = Readonly<{
  ruleId: string;
  description: string;
  sourceRefs: readonly string[];
}>;

export type EngineFactEnvelope = Readonly<{
  engine: EngineId;
  engineVersion: string;
  facts: Readonly<Record<string, unknown>>;
  ruleTrace: readonly RuleTraceEntry[];
  confidence: number;
  sourceRefs: readonly string[];
  warnings: readonly string[];
}>;

export type Citation = Readonly<{
  sourceId: string;
  title: string;
  locator: string;
}>;

export type ReportSection = Readonly<{
  id: string;
  title: string;
  body: string;
  sourceRefs: readonly string[];
}>;

export type ReportSnapshot = Readonly<{
  reportId: string;
  version: number;
  inputHash: string;
  rulesetVersion: string;
  corpusVersion: string;
  promptVersion: string;
  modelConfig: string;
  sections: readonly ReportSection[];
  citations: readonly Citation[];
  createdAt: string;
}>;

export function parseReportRequest(input: unknown): ReportRequest {
  return reportRequestSchema.parse(input);
}
